import { CheckCircle2, Minus, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  describeGranolaHealth,
  EditRemoveActions,
  minutesToHm,
  searchableSource,
  type DataGridConfig,
  type OptionSearch,
} from '@eo/shared';

import type {
  ClientItem,
  IntegrationDetail,
  OverviewCfo,
  StaffItem,
  TaskTypeItem,
} from '../api/types';
import type { AttributionList, HolidayItem } from '../api/types';
import { formatChecked, formatHours, yesNo } from '../helpers';
import { DIAGNOSTIC_STATUS_VARIANT, ROLE_LABELS } from '../constants';
import type { TimeEntryRow } from '../types';

type OverviewGridArgs = {
  date: () => string;
  probeFor: (staffId: string) => { account: string | null; problem: string | null } | null;
  onVerify: (staffId: string) => void;
  isVerifying: () => boolean;
};

export function makeOverviewGridConfig({
  date,
  probeFor,
  onVerify,
  isVerifying,
}: OverviewGridArgs): DataGridConfig<OverviewCfo> {
  return {
    getRowId: (row) => row.id,
    pagination: false,
    search: { placeholder: 'Search CFO' },
    emptyState: {
      title: 'No staff to report on.',
      description: 'Add staff under Registries and their status shows up here.',
    },
    columns: [
      {
        id: 'name',
        header: 'CFO',
        accessor: (row) => row.name,
        cell: (row) => (
          <>
            <span className="font-medium text-foreground">{row.name}</span>
            <span className="ml-2 text-xs text-muted-foreground">{row.email}</span>
          </>
        ),
      },
      {
        id: 'clickup',
        header: 'ClickUp',
        accessor: (row) => row.integrations.clickup.connected,
        cell: (row) => (
          <Detail
            label={`${row.name} ClickUp`}
            detail={row.integrations.clickup}
            account={probeFor(row.id)?.account ?? null}
            problem={probeFor(row.id)?.problem ?? null}
          />
        ),
      },
      {
        id: 'google',
        header: 'Google Calendar',
        accessor: (row) => row.integrations.google.connected,
        cell: (row) => (
          <Detail
            label={`${row.name} Google`}
            detail={row.integrations.google}
            account={row.googleAccount}
          />
        ),
      },
      {
        id: 'granola',
        header: 'Granola',
        accessor: (row) => row.integrations.granola.connected,
        cell: (row) => {
          const health = describeGranolaHealth(row.integrations.granola);
          return (
            <Detail
              label={`${row.name} Granola`}
              detail={row.integrations.granola}
              problem={health.problem}
              note={health.lastNote}
            />
          );
        },
      },
      {
        id: 'approved',
        header: 'Approved',
        align: 'right',
        accessor: (row) => row.review.approved,
        cell: (row) => <span className="tabular-nums">{row.review.approved}</span>,
      },
      {
        id: 'rejected',
        header: 'Rejected',
        align: 'right',
        accessor: (row) => row.review.rejected,
        cell: (row) => <span className="tabular-nums">{row.review.rejected}</span>,
      },
      {
        id: 'verify',
        header: '',
        align: 'right',
        accessor: () => null,
        // The probe costs one ClickUp request per token, so it is an action a
        // person takes rather than something opening this tab pays for.
        cell: (row) =>
          row.integrations.clickup.connected ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={isVerifying()}
              onClick={() => onVerify(row.id)}
              aria-label={`Verify ${row.name} ClickUp token`}
            >
              Verify
            </Button>
          ) : null,
      },
      {
        id: 'board',
        header: '',
        align: 'right',
        accessor: () => null,
        cell: (row) => (
          <Link
            to={`/?cfo=${encodeURIComponent(row.email)}&date=${date()}`}
            className="text-sm font-medium text-primary hover:underline"
          >
            Open board
          </Link>
        ),
      },
    ],
  };
}

type ClientsGridArgs = {
  onEdit: (row: ClientItem) => void;
  ownerName: (id: string | null) => string;
};

export function makeClientsGridConfig({
  onEdit,
  ownerName,
}: ClientsGridArgs): DataGridConfig<ClientItem> {
  return {
    getRowId: (row) => row.id,
    // The row is the way into everything configured about this client;
    // the concept-shaped tables still exist but no longer own the entry point.
    rowLink: (row) => `/admin/clients/${row.id}`,
    emptyState: { title: 'No clients match this view.' },
    search: { placeholder: 'Search client name' },
    columns: [
      { id: 'name', header: 'Client', accessor: (row) => row.name, sortable: true },
      { id: 'ownerUserId', header: 'Owner', accessor: (row) => ownerName(row.ownerUserId) },
      {
        id: 'isActive',
        header: 'Active',
        align: 'center',
        sortable: true,
        accessor: (row) => yesNo(row.isActive),
      },
      {
        id: 'actions',
        header: '',
        align: 'right',
        accessor: () => null,
        cell: (row) => (
          <EditRemoveActions row={row} onEdit={onEdit} editLabel={`Edit ${row.name}`} />
        ),
      },
    ],
  };
}

type StaffGridArgs = { onEdit: (row: StaffItem) => void };

export function makeStaffGridConfig({ onEdit }: StaffGridArgs): DataGridConfig<StaffItem> {
  return {
    getRowId: (row) => row.id,
    // The row is the way into everything configured about this member; the
    // concept-shaped Splits/Availability panels still exist but no longer own
    // the entry point.
    rowLink: (row) => `/admin/users/${row.id}`,
    emptyState: { title: 'No staff match this view.' },
    search: { placeholder: 'Search name or email' },
    columns: [
      { id: 'email', header: 'Email', accessor: (row) => row.email, sortable: true },
      { id: 'name', header: 'Name', accessor: (row) => row.name, sortable: true },
      {
        id: 'isAdmin',
        header: 'Admin',
        align: 'center',
        sortable: true,
        accessor: (row) => yesNo(row.isAdmin),
      },
      {
        id: 'isActive',
        header: 'Active',
        align: 'center',
        sortable: true,
        accessor: (row) => yesNo(row.isActive),
      },
      { id: 'role', header: 'Role', sortable: true, accessor: (row) => ROLE_LABELS[row.role] },
      { id: 'country', header: 'Country', sortable: true, accessor: (row) => row.country ?? '—' },
      {
        id: 'actions',
        header: '',
        align: 'right',
        accessor: () => null,
        cell: (row) => (
          <EditRemoveActions row={row} onEdit={onEdit} editLabel={`Edit ${row.email}`} />
        ),
      },
    ],
  };
}

export function makeTaskTypesGridConfig(): DataGridConfig<TaskTypeItem> {
  return {
    getRowId: (row) => row.id,
    emptyState: { title: 'No task types match this view.' },
    search: { placeholder: 'Search task type' },
    columns: [
      { id: 'name', header: 'Task type', accessor: (row) => row.name, sortable: true },
      {
        id: 'orderindex',
        header: 'Order',
        align: 'right',
        sortable: true,
        accessor: (row) => row.orderindex,
      },
      {
        id: 'isActive',
        header: 'Active',
        align: 'center',
        sortable: true,
        accessor: (row) => yesNo(row.isActive),
      },
    ],
  };
}

function Detail({
  label,
  detail,
  account,
  problem,
  note,
}: {
  label: string;
  detail: IntegrationDetail;
  account?: string | null;
  problem?: string | null;
  note?: string | null;
}) {
  if (!detail.connected) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Minus aria-label={`${label} not connected`} className="h-4 w-4" />
        Not connected
      </span>
    );
  }

  return (
    <>
      <span className="flex items-center gap-1.5 text-sm text-foreground">
        {problem ? (
          <AlertTriangle aria-label={`${label} failing`} className="h-4 w-4 text-destructive" />
        ) : (
          <CheckCircle2 aria-label={`${label} connected`} className="h-4 w-4 text-success" />
        )}
        {account ?? detail.preview}
      </span>
      {problem ? (
        <span className="mt-0.5 block text-xs text-destructive">{problem}</span>
      ) : (
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {account ? detail.preview : null}
          {detail.updatedAt ? ` · saved ${formatChecked(detail.updatedAt)}` : null}
        </span>
      )}
      {note && <span className="mt-0.5 block text-xs text-muted-foreground">{note}</span>}
    </>
  );
}

export type TimeGridArgs = {
  members: OptionSearch<StaffItem>;
  selectedLabel?: string;
  onQuery: (params: { search: string; page: number }) => void;
};

// The member filter rides `?cfo=` (their email), which the page's load reads to
// ask ClickUp for that one member instead of everyone.
export function makeTimeGridConfig({
  members,
  selectedLabel,
  onQuery,
}: TimeGridArgs): DataGridConfig<TimeEntryRow> {
  return {
    getRowId: (row) => row.id,
    filters: [
      {
        id: 'cfo',
        header: 'Member',
        filter: {
          type: 'search',
          allLabel: 'All members',
          selectedLabel,
          options: members.items.map((m) => ({ value: m.email, label: m.name })),
          ...searchableSource(members, onQuery),
        },
      },
    ],
    pagination: false,
    emptyState: { title: 'No entries logged on this date.' },
    search: { placeholder: 'Search task or client' },
    columns: [
      { id: 'cfo', header: 'CFO', accessor: (row) => row.cfo, sortable: true },
      {
        id: 'time',
        header: 'Time (ET)',
        accessor: (row) => row.time,
        cell: (row) => <span className="whitespace-nowrap text-muted-foreground">{row.time}</span>,
      },
      { id: 'taskName', header: 'Task', accessor: (row) => row.taskName, sortable: true },
      {
        id: 'client',
        header: 'Client',
        accessor: (row) => row.client,
        sortable: true,
        cell: (row) => <span className="text-muted-foreground">{row.client}</span>,
      },
      { id: 'type', header: 'Type', accessor: (row) => row.type },
      {
        id: 'durationMinutes',
        header: 'Duration',
        align: 'right',
        sortable: true,
        accessor: (row) => row.durationMinutes,
        cell: (row) => minutesToHm(row.durationMinutes),
      },
    ],
  };
}

type HolidaysGridArgs = { onRemove: (row: HolidayItem) => void };

export function makeHolidaysGridConfig({
  onRemove,
}: HolidaysGridArgs): DataGridConfig<HolidayItem> {
  return {
    getRowId: (row) => row.id,
    paramPrefix: 'hol_',
    rowTestId: 'holiday-row',
    emptyState: { title: 'No holidays configured for this year.' },
    columns: [
      { id: 'country', header: 'Country', sortable: true, accessor: (row) => row.country },
      { id: 'date', header: 'Date', sortable: true, accessor: (row) => row.date },
      { id: 'name', header: 'Name', sortable: true, accessor: (row) => row.name },
      {
        id: 'actions',
        header: '',
        align: 'right',
        accessor: () => null,
        cell: (row) => (
          <EditRemoveActions
            row={row}
            onRemove={onRemove}
            removeLabel={`Remove holiday ${row.name}`}
          />
        ),
      },
    ],
  };
}

// Column ids double as the server's sort keys. Unsorted, the server keeps its own
// order: unmapped first, then the guesses, then by hours.
export function makeAttributionGridConfig(): DataGridConfig<AttributionList> {
  return {
    getRowId: (row) => row.listName,
    rowTestId: 'attribution-list-row',
    search: { placeholder: 'Search ClickUp list or tag' },
    emptyState: { title: 'No ClickUp hours this month.' },
    columns: [
      {
        id: 'listName',
        header: 'ClickUp list or tag',
        sortable: true,
        accessor: (row) => row.listName,
      },
      {
        id: 'status',
        header: 'Matched',
        accessor: (row) => row.status,
        cell: (row) => (
          <Badge variant={DIAGNOSTIC_STATUS_VARIANT[row.status] ?? 'outline'}>{row.status}</Badge>
        ),
      },
      { id: 'client', header: 'Client', accessor: (row) => row.clientName ?? '—' },
      {
        id: 'totalHours',
        header: 'Hours',
        align: 'right',
        sortable: true,
        accessor: (row) => row.totalHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.totalHours)}</span>,
      },
    ],
  };
}
